import { Vector2 } from "three";
import * as uuid from "uuid";
import { Coordinate } from "./coordinate";

type RightOrWidth =
  | { right: number; width?: never }
  | { width: number; right?: never };

type BottomOrHeight =
  | { bottom: number; height?: never }
  | { height: number; bottom?: never };

export type RectConstructor = ConstructorParameters<typeof Rect>[0];

export class Rect {
  id = uuid.v4();
  public start: Coordinate;
  public end: Coordinate;

  changed = true;

  constructor(
    config: {
      left?: number;
      top?: number;
      id?: string;
    } & RightOrWidth &
      BottomOrHeight,
  ) {
    if ("id" in config) {
      this.id = config.id;
    }
    const left = "left" in config ? config.left : 0;
    const top = "top" in config ? config.top : 0;
    let right = 0;
    let bottom = 0;
    if ("width" in config) {
      right = left + config.width;
    }
    if ("right" in config) {
      right = config.right;
    }
    if ("height" in config) {
      bottom = top + config.height;
    }
    if ("bottom" in config) {
      bottom = config.bottom;
    }
    this.start = new Coordinate(left, top);
    this.end = new Coordinate(right, bottom);
    this.__changed();
  }

  has(config, prop): boolean {
    return prop in config;
  }

  toString() {
    return `Rect (${this.id}) (${this.x}, ${this.y}, ${this.right}, ${this.bottom}), (${this.width}x${this.height})`;
  }



  get units() {
    return {
      x: (val) => {
        return val * (this.width / 100);
      },
      y: (val) => {
        return val * (this.height / 100);
      },
    };
  }

  get x() {
    return this.start.x;
  }

  set x(x: number) {
    const width = this.width;
    this.start.x = x;
    this.width = width;
    this.__changed();
  }

  get y() {
    return this.start.y;
  }
  set y(y: number) {
    const height = this.height;
    this.start.y = y;
    this.height = height;
    this.__changed();
  }

  get right() {
    return this.end.x;
  }
  set right(x: number) {
    this.end.x = x;
    this.__changed();
  }

  get left() {
    return this.start.x;
  }
  set left(x: number) {
    this.start.x = x;
    this.__changed();
  }

  get bottom() {
    return this.end.y;
  }
  set bottom(y: number) {
    this.end.y = y;
  }
  get top() {
    return this.start.y;
  }
  set top(y: number) {
    this.start.y = y;
  }

  get width() {
    return this.end.x - this.start.x;
  }
  set width(width: number) {
    this.end.x = this.start.x + width;
    this.__changed();
  }
  get height() {
    return this.end.y - this.start.y;
  }
  set height(height: number) {
    this.end.y = this.start.y + height;
    this.__changed();
  }

  __changed() {
    this.changed = true;
  }

  static FromSquare(x, y, width) {
    return new Rect({
      left: x,
      top: y,
      width: width,
      height: width,
    });
  }

  static FromUnitRect(container: Rect, r: Rect): Rect {
    const units = container.units;
    const newRect = new Rect({ left: 0, top: 0, right: 0, bottom: 0 });
    newRect.left = units.x(r.left);
    newRect.right = units.x(r.right);
    newRect.top = units.y(r.top);
    newRect.bottom = units.y(r.bottom);
    return newRect;
  }

  static FromUnitScalar(scalar: number, r: Rect): Rect {
    const newRect = new Rect({ left: 0, top: 0, right: 0, bottom: 0 });
    newRect.left = r.left * scalar;
    newRect.right = r.right * scalar;
    newRect.top = r.top * scalar;
    newRect.bottom = r.bottom * scalar;
    return newRect;
  }

  static FromLTWH(left: number, top: number, width: number, height: number) {
    return new Rect({
      left,
      top,
      width,
      height,
    });
  }

  static FromLTRB(left: number, top: number, right: number, bottom: number) {
    return new Rect({ left, top, right, bottom });
  }

  intersects(r2: Rect) {
    if (
      r2.left > this.right ||
      r2.right < this.left ||
      r2.top > this.bottom ||
      r2.bottom < this.top
    )
      return false;
    return true;
  }

  intersectsPoint(point: Vector2) {
    if (
      point.x > this.right ||
      point.x < this.left ||
      point.y > this.bottom ||
      point.y < this.top
    )
      return false;
    return true;
  }
}
